

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('genkey_token');
      const storedUser = localStorage.getItem('genkey_user');
      
      if (token && storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
          
          // Verify token is still valid
          try {
            await api.get('/auth/me');
          } catch (error) {
            // Token expired or invalid
            if (error.response?.status === 401 || error.response?.status === 403) {
              localStorage.removeItem('genkey_token');
              localStorage.removeItem('genkey_user');
              setUser(null);
            }
          }
        } catch (error) {
          localStorage.removeItem('genkey_token');
          localStorage.removeItem('genkey_user');
          setUser(null);
        }
      }
      setLoading(false);
    };
    
    initAuth();

    // Clear session when tab is closed
    const handleBeforeUnload = () => {
      localStorage.removeItem('genkey_token');
      localStorage.removeItem('genkey_user');
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const login = useCallback(async (username, password) => {
    const res = await api.post('/auth/login', { username, password });
    const { token, user: userData } = res.data;
    localStorage.setItem('genkey_token', token);
    localStorage.setItem('genkey_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  }, []);

  const ssoLogin = useCallback(async (token) => {
    const res = await api.post('/auth/sso-login', { token });
    const { token: jwtToken, user: userData } = res.data;
    localStorage.setItem('genkey_token', jwtToken);
    localStorage.setItem('genkey_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('genkey_token');
    localStorage.removeItem('genkey_user');
    setUser(null);
  }, []);

  const isAdmin = user?.role === 'admin';

  return (
   <AuthContext.Provider value={{ user, loading, login, ssoLogin, logout, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};